/**
 * The Letter Boxed Solver: valid words for a four-sided box, and chains of them.
 *
 * Rules, as the puzzle states them: letters sit on the four sides of a square; a word is
 * built by moving from letter to letter, and two consecutive letters may never come from
 * the same side; a word has at least three letters; the next word starts with the last
 * letter of the one before; and the aim is to use every letter of the box, in as few
 * words as possible. Letters (and words) may be reused only as the rules allow — a
 * letter may appear any number of times, a word only once.
 *
 * Each valid word is reduced to its first letter, last letter and a 26-bit mask of the
 * letters it uses. A chain is complete when the union of its masks equals the box's.
 * Chains are searched depth-first with two prunings: a chain is only counted at the
 * length where it first covers the box (no padding with a redundant word), and the final
 * word is looked up in an index of "starts with X and contains Y" so that only words
 * able to supply a still-missing letter are tried.
 */

// biome-ignore-start lint/suspicious/noBitwiseOperators: a letter set is a 26-bit mask, one bit per letter

export const SIDE_COUNT = 4;
export const MAX_PER_SIDE = 5;
export const MIN_WORD_LENGTH = 3;

const LETTER_A = 97;
const ALPHABET = 26;
const RE_SEPARATORS = /[\s,]+/g;
const RE_LETTERS = /^[a-z]*$/;

export interface Box {
  /** The letters of each side, as typed. */
  sides: string[][];
  /** Which side (0–3) each letter a–z is on, or -1. */
  sideOf: Int8Array;
  /** Bitmask of every letter in the box. */
  full: number;
  /** Number of letters in the box. */
  size: number;
}

export type BoxParse =
  | { ok: true; box: Box }
  | {
      ok: false;
      reason: "empty" | "invalid" | "incomplete" | "duplicate" | "too-many";
    };

const bitOf = (code: number) => 1 << (code - LETTER_A);

/** Read the four sides. Spaces and commas are ignored; every letter must be distinct. */
export function parseBox(sideTexts: readonly string[]): BoxParse {
  const sides = sideTexts.map((text) =>
    Array.from(text.toLowerCase().replace(RE_SEPARATORS, ""))
  );
  if (sides.every((side) => side.length === 0)) {
    return { ok: false, reason: "empty" };
  }
  if (!sides.every((side) => RE_LETTERS.test(side.join("")))) {
    return { ok: false, reason: "invalid" };
  }
  if (sides.length !== SIDE_COUNT || sides.some((side) => side.length === 0)) {
    return { ok: false, reason: "incomplete" };
  }
  if (sides.some((side) => side.length > MAX_PER_SIDE)) {
    return { ok: false, reason: "too-many" };
  }
  const all = sides.flat();
  if (new Set(all).size !== all.length) {
    return { ok: false, reason: "duplicate" };
  }
  const sideOf = new Int8Array(ALPHABET).fill(-1);
  let full = 0;
  sides.forEach((side, index) => {
    for (const letter of side) {
      sideOf[letter.charCodeAt(0) - LETTER_A] = index;
      full |= bitOf(letter.charCodeAt(0));
    }
  });
  return { ok: true, box: { sides, sideOf, full, size: all.length } };
}

export interface BoxWord {
  word: string;
  /** First letter, 0–25. */
  first: number;
  /** Last letter, 0–25. */
  last: number;
  mask: number;
  /** Number of distinct box letters the word uses. */
  covers: number;
}

function popcount(value: number): number {
  let n = value;
  let count = 0;
  while (n) {
    n &= n - 1;
    count += 1;
  }
  return count;
}

/** Words that obey the box: only box letters, never two in a row from one side. */
export function validWords(
  list: readonly string[],
  box: Box,
  minLength = MIN_WORD_LENGTH
): BoxWord[] {
  const found: BoxWord[] = [];
  for (const word of list) {
    if (word.length < minLength) {
      continue;
    }
    let mask = 0;
    let previous = -1;
    let ok = true;
    for (let i = 0; i < word.length; i += 1) {
      const code = word.charCodeAt(i) - LETTER_A;
      const side = code >= 0 && code < ALPHABET ? box.sideOf[code] : -1;
      if (side < 0 || side === previous) {
        ok = false;
        break;
      }
      previous = side;
      mask |= 1 << code;
    }
    if (ok) {
      found.push({
        word,
        first: word.charCodeAt(0) - LETTER_A,
        last: word.charCodeAt(word.length - 1) - LETTER_A,
        mask,
        covers: popcount(mask),
      });
    }
  }
  return found;
}

export interface Chain {
  words: string[];
  /** Total letters across the words. */
  letters: number;
}

export interface ChainSearch {
  chains: Chain[];
  /** Chains found (equals `chains.length` unless the cap cut the list). */
  total: number;
  /** True when the search stopped early, at the cap or the work budget. */
  truncated: boolean;
}

export interface SearchOptions {
  /** Number of words in each chain. */
  length: number;
  /** Stop after collecting this many chains. */
  limit: number;
  /** Stop after this many word-pair checks (guards the three-word search). */
  budget: number;
  /** Only chains that begin with this word. */
  start?: string;
}

function lowestBit(value: number): number {
  return 31 - Math.clz32(value & -value);
}

interface Index {
  sorted: BoxWord[];
  byFirst: Map<number, BoxWord[]>;
  /** Words that start with letter X and contain letter Y, keyed `X * 32 + Y`. */
  byFirstHas: Map<number, BoxWord[]>;
  maxCovers: number;
}

function buildIndex(words: readonly BoxWord[]): Index {
  const sorted = [...words].sort(
    (a, b) => b.covers - a.covers || a.word.localeCompare(b.word)
  );
  const byFirst = new Map<number, BoxWord[]>();
  const byFirstHas = new Map<number, BoxWord[]>();
  for (const entry of sorted) {
    const list = byFirst.get(entry.first) ?? [];
    list.push(entry);
    byFirst.set(entry.first, list);
    for (let letter = 0; letter < ALPHABET; letter += 1) {
      if (entry.mask & (1 << letter)) {
        const key = entry.first * 32 + letter;
        const indexed = byFirstHas.get(key) ?? [];
        indexed.push(entry);
        byFirstHas.set(key, indexed);
      }
    }
  }
  const maxCovers = sorted.reduce(
    (best, entry) => Math.max(best, entry.covers),
    0
  );
  return { sorted, byFirst, byFirstHas, maxCovers };
}

/**
 * Chains of exactly `length` words that cover the box on the final word and not before.
 * Words come most-covering first, so a capped search finds the efficient chains early.
 */
export function findChains(
  words: readonly BoxWord[],
  box: Box,
  options: SearchOptions
): ChainSearch {
  const index = buildIndex(words);
  const start = options.start
    ? index.sorted.find((entry) => entry.word === options.start)
    : undefined;
  if (options.start && !start) {
    return { chains: [], total: 0, truncated: false };
  }
  const chains: Chain[] = [];
  const trail: BoxWord[] = [];
  let work = 0;
  let stopped = false;

  function candidates(mask: number): readonly BoxWord[] {
    const previous = trail.at(-1);
    if (!previous) {
      return start ? [start] : index.sorted;
    }
    if (trail.length === options.length - 1) {
      const missing = box.full & ~mask;
      return missing
        ? (index.byFirstHas.get(previous.last * 32 + lowestBit(missing)) ?? [])
        : [];
    }
    return index.byFirst.get(previous.last) ?? [];
  }

  /** Can `entry` extend the trail — new word, right coverage, still reachable? */
  function fits(entry: BoxWord, mask: number): boolean {
    if (trail.some((used) => used.word === entry.word)) {
      return false;
    }
    const next = mask | entry.mask;
    const isLast = trail.length === options.length - 1;
    if (isLast !== (next === box.full)) {
      return false;
    }
    const wordsLeft = options.length - trail.length - 1;
    return popcount(box.full & ~next) <= wordsLeft * index.maxCovers;
  }

  function extend(mask: number) {
    for (const entry of candidates(mask)) {
      work += 1;
      if (work > options.budget) {
        stopped = true;
      }
      if (stopped) {
        return;
      }
      if (!fits(entry, mask)) {
        continue;
      }
      trail.push(entry);
      if (trail.length === options.length) {
        chains.push({
          words: trail.map((word) => word.word),
          letters: trail.reduce((sum, word) => sum + word.word.length, 0),
        });
        stopped = chains.length >= options.limit;
      } else {
        extend(mask | entry.mask);
      }
      trail.pop();
      if (stopped) {
        return;
      }
    }
  }

  extend(0);
  chains.sort(
    (a, b) =>
      a.letters - b.letters ||
      a.words.join(" ").localeCompare(b.words.join(" "))
  );
  return { chains, total: chains.length, truncated: stopped };
}

// biome-ignore-end lint/suspicious/noBitwiseOperators: end of the mask code
